import { previewObjectivesAction, type AnalyticsEvent } from '../../core/events';
import { useId, useState } from 'react';
import {
  ACADEMIC_SUBJECTS,
  GRADE_LEVELS,
  JURISDICTIONS,
  type AcademicSubject,
  type GradeLevel,
  type Jurisdiction,
  type Profile,
} from '../../core/profile';
import { completeProfile, type StandardsDefault } from '../../core/standards-default';

/**
 * Where you teach, what, and to whom. The three fields are the Learning Commons filters
 * verbatim, so the options come straight from the core enums rather than being retyped —
 * a value that would not survive the API cannot be picked here.
 *
 * `initial` may be partial: the shared standards default another Labs tool filled in part
 * of. Fields it carries open filled in, fields it lacks open empty, and the form cannot be
 * saved until all three are chosen. With no `initial` at all the form opens as it always
 * has, on Texas and Mathematics.
 */

export interface ProfileFormProps {
  initial?: StandardsDefault | null;
  saving?: boolean;
  error?: { field?: string; message: string } | null;
  onSave: (profile: Profile) => void;
  onTrack?: (event: AnalyticsEvent) => void;
}

export default function ProfileForm({ initial, saving = false, error, onSave, onTrack }: ProfileFormProps) {
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction | ''>(
    initial ? initial.jurisdiction ?? '' : 'Texas',
  );
  const [academicSubject, setAcademicSubject] = useState<AcademicSubject | ''>(
    initial ? initial.academicSubject ?? '' : 'Mathematics',
  );
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>(initial?.gradeLevels ?? []);
  const profile = completeProfile({
    ...(jurisdiction ? { jurisdiction } : {}),
    ...(academicSubject ? { academicSubject } : {}),
    gradeLevels,
  });

  const stateId = useId();
  const subjectId = useId();
  const messageId = useId();

  const toggleGrade = (grade: GradeLevel) => {
    const next = gradeLevels.includes(grade)
      ? gradeLevels.filter((g) => g !== grade)
      : GRADE_LEVELS.filter((g) => g === grade || gradeLevels.includes(g));
    setGradeLevels(next);
    onTrack?.(previewObjectivesAction('grades_selected', { grades: [...next] }));
  };

  return (
    <form
      className="panel"
      onSubmit={(event) => {
        event.preventDefault();
        if (saving || !profile) return;
        onSave(profile);
      }}
      noValidate
    >
      <h2>What do you teach?</h2>
      <p className="hint">
        Choose your state, subject, and grade levels. We’ll use these to match your
        lesson’s objectives to academic standards.
      </p>

      <div className="field-row">
        <label htmlFor={stateId}>State</label>
        <select
          id={stateId}
          value={jurisdiction}
          onChange={(e) => {
            const state = e.target.value as Jurisdiction;
            setJurisdiction(state);
            onTrack?.(previewObjectivesAction('states_selected', { state }));
          }}
          disabled={saving}
        >
          {jurisdiction === '' && <option value="" disabled>Choose a state</option>}
          {JURISDICTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <div className="field-row">
        <label htmlFor={subjectId}>Subject</label>
        <select
          id={subjectId}
          value={academicSubject}
          onChange={(e) => {
            const subject = e.target.value as AcademicSubject;
            setAcademicSubject(subject);
            onTrack?.(previewObjectivesAction('subjects_selected', { subject }));
          }}
          disabled={saving}
        >
          {academicSubject === '' && <option value="" disabled>Choose a subject</option>}
          {ACADEMIC_SUBJECTS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <fieldset className="grades">
        <legend>Grade level</legend>
        {/* `.grade`, not `.chip`: the report's filter buttons own that name, and a shared
            class put a second bordered pill around every one of these. */}
        <div className="grade-list">
          {GRADE_LEVELS.map((grade) => (
            <label key={grade} className="grade">
              <input
                type="checkbox"
                checked={gradeLevels.includes(grade)}
                onChange={() => toggleGrade(grade)}
                disabled={saving}
              />
              <span>{grade}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <p className="message" id={messageId} role="status" aria-live="polite" data-tone={error ? 'error' : undefined}>
        {error?.message ?? ''}
      </p>

      <button className="primary" type="submit" disabled={saving || !profile}>
        {saving ? 'Saving…' : 'Save and continue'}
      </button>
    </form>
  );
}
